import { formatPercent } from 'utils/Helpers';
import {
  hasUrgencyDiscountAmount,
  isUrgencyDiscountType,
  shouldShowUrgencyDiscountAvailability,
} from 'utils/urgencyDiscount';
import { hasRoundTripDiscount } from 'utils/Reserbus';

/**
 * Resolves the active pricing key for the current trip list context.
 *
 * @param {object} trip - Trip result.
 * @param {boolean} roundTrip - Whether the search is round trip.
 * @param {string} way - Current trip way.
 * @returns {string} Pricing key to read from the trip.
 */
const getDeparturePricingKey = (trip, roundTrip, way) => {
  if (!roundTrip) return 'pricing';

  const roundTripPricingKey =
    way === 'departure' ? 'departureRoundTripPricing' : 'roundTripPricing';
  return trip[roundTripPricingKey] ? roundTripPricingKey : 'pricing';
};

/**
 * Converts a value to a finite number.
 *
 * @param {*} value - Value to convert.
 * @returns {number|null} Parsed number or null when invalid.
 */
const getNumericValue = (value) => {
  if (value === null || value === undefined) return null;

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

/**
 * Aligns return trips with the pricing selection used by TripList.
 *
 * @param {object} params - Filtering parameters.
 * @param {Array} params.trips - Trips represented by the result list.
 * @param {string} params.way - Current trip direction.
 * @param {boolean} params.validateRoundTripDiscount - Whether compatibility validation is enabled.
 * @param {object|null} params.departureTrip - Selected departure trip.
 * @returns {Array} Trips with incompatible round-trip pricing removed.
 */
export const getTripsForDiscountStats = ({
  trips,
  way,
  validateRoundTripDiscount,
  departureTrip,
}) => {
  if (way !== 'return' || !validateRoundTripDiscount) return trips;
  if (!departureTrip) return [];

  return trips.map((trip) => {
    if (hasRoundTripDiscount(departureTrip, trip)) return trip;

    return { ...trip, roundTripPricing: undefined };
  });
};

/**
 * Reads the provider discount percent from pricing.
 *
 * @param {object} pricing - Trip pricing.
 * @returns {number} Discount percent or 0 when unavailable.
 */
const getDiscountPercent = (pricing) => {
  const providerDiscount = pricing?.providerDiscount;
  const providerPercent = getNumericValue(providerDiscount?.percent);

  if (providerPercent !== null && providerPercent > 0) return providerPercent;

  const discountAmount = getNumericValue(providerDiscount?.amount);
  const total = getNumericValue(pricing?.total);

  if (discountAmount === null || total === null) return 0;

  const totalBeforeDiscount =
    getNumericValue(pricing?.totalBeforeDiscount) ?? total + discountAmount;
  const calculatedPercent = formatPercent(totalBeforeDiscount, total).replace('%', '');

  return getNumericValue(calculatedPercent) ?? 0;
};

/**
 * Calculates sold tickets using trip or category capacity.
 *
 * @param {object} trip - Trip result.
 * @returns {number} Estimated number of sold tickets.
 */
const getTripSoldTickets = (trip) => {
  const capacity = getNumericValue(trip.capacity);
  const availability = getNumericValue(trip.availability);

  if (capacity !== null && availability !== null) {
    return Math.max(0, capacity - availability);
  }

  return (trip.passengerTypes || []).reduce((soldTickets, passengerType) => {
    const categoryCapacity = getNumericValue(passengerType.capacity);
    const categoryAvailability = getNumericValue(passengerType.availability);

    if (categoryCapacity === null || categoryAvailability === null) return soldTickets;

    return soldTickets + Math.max(0, categoryCapacity - categoryAvailability);
  }, 0);
};

/**
 * Adds a discount availability value to its type aggregate.
 *
 * @param {object} availabilityTypes - Availability accumulated by discount type.
 * @param {string} discountType - Discount type or provider discount name.
 * @param {*} availability - Discount availability value.
 * @returns {object} Updated availability aggregate.
 */
const addDiscountAvailability = (availabilityTypes, discountType, availability) => {
  if (!discountType || availability === null || availability === undefined) {
    return availabilityTypes;
  }

  const numericAvailability = getNumericValue(availability);
  if (numericAvailability === null || numericAvailability < 0) return availabilityTypes;

  return {
    ...availabilityTypes,
    [discountType]: (availabilityTypes[discountType] || 0) + numericAvailability,
  };
};

/**
 * Aggregates active and alternative discount availability from pricing.
 *
 * @param {object} pricing - Trip pricing.
 * @param {object} availabilityTypes - Availability accumulated by discount type.
 * @returns {object} Updated availability aggregate.
 */
const getDiscountAvailabilityTypes = (pricing, availabilityTypes) => {
  const discountType = pricing?.discountType || pricing?.discount_type;
  const discountAvailability = pricing?.discountAvailability ?? pricing?.discount_availability;
  const providerDiscounts = pricing?.providerDiscounts || pricing?.provider_discounts || [];
  const activeDiscountAvailabilityTypes = addDiscountAvailability(
    availabilityTypes,
    discountType,
    discountAvailability,
  );

  return providerDiscounts.reduce(
    (types, providerDiscount) =>
      addDiscountAvailability(types, providerDiscount.name, providerDiscount.availability),
    activeDiscountAvailabilityTypes,
  );
};

/**
 * Builds aggregate stats for the discount urgency banner.
 *
 * @param {object} params - Stats params.
 * @param {Array} params.trips - Trip results.
 * @param {boolean} params.roundTrip - Whether the search is round trip.
 * @param {string} params.way - Current trip way.
 * @returns {object} Promo urgency aggregate stats.
 */
const getDiscountUrgencyPromoStats = ({ trips, roundTrip, way }) =>
  trips.reduce(
    (stats, trip) => {
      if (trip.isAd) return stats;

      const pricingKey = getDeparturePricingKey(trip, roundTrip, way);
      const pricing = trip[pricingKey];
      const discountType = pricing?.discountType || pricing?.discount_type;
      const discountAvailabilityTypes = getDiscountAvailabilityTypes(
        pricing,
        stats.discountAvailabilityTypes,
      );

      if (
        !hasUrgencyDiscountAmount(pricing?.providerDiscount) ||
        !isUrgencyDiscountType(discountType)
      ) {
        return { ...stats, discountAvailabilityTypes };
      }

      const discountAvailability = getNumericValue(
        pricing?.discountAvailability ?? pricing?.discount_availability,
      );
      const hasLowAvailability = shouldShowUrgencyDiscountAvailability(discountAvailability);
      const originName = trip.origin?.cityName || trip.origin?.name || '';
      const destinationName = trip.destination?.cityName || trip.destination?.name || '';

      return {
        scheduleCount: stats.scheduleCount + 1,
        discountPercent: Math.max(stats.discountPercent, getDiscountPercent(pricing)),
        ticketsSold: stats.ticketsSold + getTripSoldTickets(trip),
        lowAvailabilityScheduleCount:
          stats.lowAvailabilityScheduleCount + (hasLowAvailability ? 1 : 0),
        lowAvailabilityTickets:
          stats.lowAvailabilityTickets + (hasLowAvailability ? discountAvailability : 0),
        minimumAvailability: hasLowAvailability
          ? Math.min(stats.minimumAvailability ?? discountAvailability, discountAvailability)
          : stats.minimumAvailability,
        maximumAvailability: hasLowAvailability
          ? Math.max(stats.maximumAvailability ?? discountAvailability, discountAvailability)
          : stats.maximumAvailability,
        currentRoute:
          stats.currentRoute ||
          (originName && destinationName ? `${originName} - ${destinationName}` : ''),
        discountAvailabilityTypes,
      };
    },
    {
      scheduleCount: 0,
      discountPercent: 0,
      ticketsSold: 0,
      lowAvailabilityScheduleCount: 0,
      lowAvailabilityTickets: 0,
      minimumAvailability: null,
      maximumAvailability: null,
      currentRoute: '',
      discountAvailabilityTypes: {},
    },
  );

export default getDiscountUrgencyPromoStats;
